import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, DollarSign, Trash2, ArrowDown, ArrowUp, ChevronRight, ChevronDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const fmt = (n) => `$${Math.abs(Number(n || 0)).toFixed(2)}`;

export default function SettleDebtDialog({
  open, onOpenChange, driverId, driverName, outstanding, periodStart, periodEnd, onSaved,
}) {
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  const [breakdown, setBreakdown] = useState(null);

  // Aggregate history by local Naples-EDT day: { "2026-07-29": {total, items[]} }
  const dailyGroups = (() => {
    const groups = {};
    for (const s of history) {
      if (!s.created_at) continue;
      // Naples EDT day key (YYYY-MM-DD in America/New_York)
      const dayKey = new Date(s.created_at).toLocaleDateString("en-CA", { timeZone: "America/New_York" });
      if (!groups[dayKey]) groups[dayKey] = { total: 0, items: [] };
      groups[dayKey].total += Number(s.amount) || 0;
      groups[dayKey].items.push(s);
    }
    // Sort days most-recent first
    return Object.entries(groups).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  })();

  // Sign of the outstanding balance dictates the flow direction of THIS settlement.
  // outstanding > 0  → driver owes LLC → we register a POSITIVE settlement (driver pays LLC).
  // outstanding < 0  → LLC owes driver → we register a NEGATIVE settlement (LLC pays driver).
  const owedToDriver = Number(outstanding || 0) < -0.005;
  // If the breakdown reports a `total_true_outstanding` bigger than the
  // period-scoped `outstanding` prop (because the driver still has UNPAID
  // debt from days BEFORE the current period that any new payment would
  // FIFO-cover first), use it instead. This is what caused the "Koky pagó
  // $318.35 y aún debe $22.10" scenario — the pool applied oldest-first and
  // silently drained into pre-period residual, leaving the period unclosed.
  // Only override when the sign of `outstanding` is positive (driver-owes-
  // LLC flow); the LLC-owes-driver flow ignores pre-period debt because
  // paying money OUT to the driver won't zero out old positive-debt days.
  const trueOutstanding = (!owedToDriver && breakdown && breakdown.total_true_outstanding > Math.abs(Number(outstanding || 0)))
    ? Number(breakdown.total_true_outstanding)
    : Math.abs(Number(outstanding || 0));
  const prePeriodUnpaid = Number((breakdown && breakdown.pre_period_unpaid) || 0);
  const periodRemaining = Number((breakdown && breakdown.period_remaining) || 0);
  const absOutstanding = trueOutstanding;

  useEffect(() => {
    if (!open) return;
    setAmount(absOutstanding > 0.005 ? absOutstanding.toFixed(2) : "");
    setNotes("");
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, outstanding, driverId, periodStart, periodEnd, breakdown]);

  const loadHistory = async () => {
    if (!driverId) return;
    setLoadingHist(true);
    try {
      // Always request the driver's WHOLE settlement history — filtering by
      // the currently-selected period would hide payments made today that
      // FIFO-cover last-week's debt, which is exactly the scenario the
      // dispatcher needs to confirm.
      const params = new URLSearchParams({ driver_id: driverId });
      const { data } = await api.get(`/settlements?${params.toString()}`);
      setHistory(Array.isArray(data) ? data : []);
      // Per-day breakdown with FIFO allocation
      if (periodStart && periodEnd) {
        try {
          const bdParams = new URLSearchParams({ driver_id: driverId, start: periodStart, end: periodEnd });
          const { data: bd } = await api.get(`/settlements/breakdown?${bdParams.toString()}`);
          setBreakdown(bd);
        } catch (_) { setBreakdown(null); }
      }
    } catch (e) {
      console.warn("SettleDebtDialog.loadHistory failed:", e);
    } finally {
      setLoadingHist(false);
    }
  };

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error("Ingresa un monto mayor a 0");
      return;
    }
    // Negative amount when LLC pays driver, positive when driver pays LLC
    const signedAmount = owedToDriver ? -amt : amt;
    setBusy(true);
    try {
      await api.post("/settlements", {
        driver_id: driverId,
        amount: signedAmount,
        notes,
        period_start: periodStart,
        period_end: periodEnd,
      });
      toast.success(
        owedToDriver
          ? `Se registró entrega de ${fmt(amt)} a ${driverName}`
          : `Deuda de ${driverName} liquidada por ${fmt(amt)}`
      );
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeHistory = async (id) => {
    if (!window.confirm("¿Eliminar esta liquidación? Se revertirá el saldo.")) return;
    try {
      await api.delete(`/settlements/${id}`);
      toast.success("Liquidación eliminada");
      await loadHistory();
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const boxBorder = owedToDriver ? "border-cyan-500/30" : "border-emerald-500/30";
  const boxBg = owedToDriver ? "bg-cyan-500/5" : "bg-emerald-500/5";
  const amountColor = owedToDriver ? "text-cyan-400" : "text-emerald-400";
  const submitClass = owedToDriver
    ? "bg-cyan-500 hover:bg-cyan-500/90 text-black font-bold"
    : "bg-emerald-500 hover:bg-emerald-500/90 text-black font-bold";
  const submitLabel = owedToDriver ? "Registrar entrega" : "Registrar pago";
  const submitIcon = owedToDriver ? ArrowDown : CheckCircle2;
  const SubmitIcon = submitIcon;
  const amountLabel = owedToDriver ? "Monto entregado al chofer" : "Monto recibido del chofer";
  const notesPlaceholder = owedToDriver
    ? "Ej: Entregado en efectivo al chofer el viernes"
    : "Ej: Recibido en efectivo el viernes";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] flex flex-col p-0 gap-0" data-testid="settle-debt-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-border/50 flex-shrink-0">
          <DialogTitle style={{ fontFamily: "Manrope" }} className="text-2xl flex items-center gap-2">
            <DollarSign className={`w-5 h-5 ${amountColor}`} /> {owedToDriver ? "Entregar al chofer" : "Liquidar deuda"}
          </DialogTitle>
          <div className="text-sm text-muted-foreground pt-1">
            Chofer: <span className="text-white font-semibold">{driverName}</span>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="settle-body">
        <div className={`rounded-md border ${boxBorder} ${boxBg} p-3 space-y-1`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            {owedToDriver ? "Saldo a favor del chofer" : "Deuda total a liquidar"}
          </div>
          <div className={`text-3xl font-black font-mono ${amountColor}`} data-testid="settle-outstanding">
            {fmt(absOutstanding)}
          </div>
          {/* Break the total into "period" vs "pre-period residual" so the
              dispatcher understands why the number can be larger than what
              the reports table shows for the selected week. */}
          {!owedToDriver && prePeriodUnpaid > 0.005 && (
            <div className="text-[11px] font-mono pt-1 space-y-0.5" data-testid="settle-breakdown-summary">
              <div className="flex gap-2">
                <span className="text-muted-foreground w-32">del período:</span>
                <span className="text-white font-bold">{fmt(periodRemaining)}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-amber-400/90 w-32">deuda anterior:</span>
                <span className="text-amber-300 font-bold">+ {fmt(prePeriodUnpaid)}</span>
              </div>
              <div className="text-[10px] text-amber-400/80 pt-1 leading-tight">
                ⚠️ El pool de liquidaciones aplica primero a la deuda más vieja (FIFO). Para dejar TODO en $0.00 el chofer debe entregar el total mostrado arriba, no solo la deuda del período.
              </div>
            </div>
          )}
          <div className="text-[10px] text-muted-foreground">
            {owedToDriver
              ? "La empresa debe al chofer este monto (Zelle a LLC excedió lo que él debía entregar). Al registrar la entrega, el saldo quedará en $0.00."
              : "Al registrar el cobro, esta deuda quedará en $0.00 (los totales LLC de la semana se mantienen)."}
          </div>
        </div>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label>{amountLabel}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="settle-amount-input"
              className="bg-input border-border font-mono text-lg font-bold"
            />
          </div>
          <div className="space-y-1">
            <Label>Notas (opcional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={notesPlaceholder}
              data-testid="settle-notes-input"
              className="bg-input border-border"
              rows={2}
            />
          </div>
        </div>

        {/* Per-day breakdown */}
        {breakdown && breakdown.days && breakdown.days.length > 0 && (
          <div className="border-t border-border pt-3">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">
              Desglose por día (settlements FIFO)
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1 text-xs" data-testid="settle-breakdown">
              {breakdown.days.map((r) => {
                const [y, m, d] = r.date.split("-");
                // A day is CLEARED when the settlements pool covered its
                // positive debt (remaining ≈ 0 and debt was positive).
                // A day is NEGATIVE when it starts Zelle/investment-heavy
                // (LLC owes the chofer for that day) → shown in cyan with
                // a minus sign and it OFFSETS the total to liquidate.
                const paid = r.remaining < 0.005 && r.debt > 0.005;
                const isNegDay = r.debt < -0.005;
                // Signed formatter — preserves the "-" sign so a Zelle-heavy
                // day reads "-$6.00" instead of "$6.00", matching the sign
                // convention in the reports table.
                const fmtSigned = (n) => {
                  const v = Number(n || 0);
                  return v < 0 ? `-$${Math.abs(v).toFixed(2)}` : `$${v.toFixed(2)}`;
                };
                const remStyle = isNegDay
                  ? "text-cyan-400"
                  : (paid ? "text-emerald-400" : "text-amber-400");
                return (
                  <div key={r.date} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-secondary/40" data-testid={`breakdown-${r.date}`}>
                    <span className="font-mono text-[10px] w-20 tabular-nums text-muted-foreground">{d}/{m}/{y}</span>
                    <span className="text-[10px] text-muted-foreground w-16">{r.trips} viaje{r.trips !== 1 ? "s" : ""}</span>
                    <span
                      className={`text-[10px] w-24 font-mono ${isNegDay ? "text-cyan-400" : "text-muted-foreground"}`}
                      title={isNegDay ? "Día Zelle/inversión-heavy — LLC debe al chofer este monto" : undefined}
                    >
                      gen {fmtSigned(r.debt)}
                    </span>
                    {r.applied > 0.005 && (
                      <span className="text-[10px] text-emerald-400 w-24 font-mono">− {fmt(r.applied)}</span>
                    )}
                    <span className={`ml-auto font-mono font-bold ${remStyle}`}>
                      {paid ? "✓ $0.00" : fmtSigned(r.remaining)}
                    </span>
                  </div>
                );
              })}
              {breakdown.leftover_credit > 0.005 && (
                <div className="text-[10px] text-cyan-400 px-2 pt-1">
                  Crédito sobrante del chofer: {fmt(breakdown.leftover_credit)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* History */}
        <div className="border-t border-border pt-3">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">
            Todas las liquidaciones del chofer {loadingHist && <span>· cargando…</span>}
          </div>
          {!loadingHist && history.length === 0 && (
            <div className="text-xs text-muted-foreground py-2">Sin liquidaciones registradas.</div>
          )}
          <div className="max-h-56 overflow-y-auto space-y-1" data-testid="settle-history">
            {dailyGroups.map(([dayKey, { total, items }]) => {
              const isNeg = total < -0.005;
              const isOpen = !!expandedDays[dayKey];
              const [y, m, d] = dayKey.split("-");
              // Display as DD/MM/YYYY (Spanish convention)
              const dateLabel = `${d}/${m}/${y}`;
              return (
                <div key={dayKey} className="rounded border border-border/50" data-testid={`settle-day-${dayKey}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedDays((e) => ({ ...e, [dayKey]: !e[dayKey] }))}
                    className="w-full flex items-center gap-2 text-xs py-1.5 px-2 hover:bg-secondary/40"
                    data-testid={`settle-day-toggle-${dayKey}`}
                  >
                    {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    {isNeg
                      ? <ArrowDown className="w-3 h-3 text-cyan-400" />
                      : <ArrowUp className="w-3 h-3 text-emerald-400" />}
                    <span className={`font-mono font-bold w-24 text-left ${isNeg ? "text-cyan-400" : "text-emerald-400"}`}>
                      {fmt(total)}
                    </span>
                    <span className="text-muted-foreground text-[10px] font-mono tabular-nums">{dateLabel}</span>
                    <span className="ml-auto text-[10px] text-muted-foreground">
                      {items.length === 1 ? "1 liquidación" : `${items.length} liquidaciones`}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="border-t border-border/40 divide-y divide-border/30">
                      {items.map((s) => {
                        const itemNeg = Number(s.amount) < 0;
                        return (
                          <div
                            key={s.id}
                            className="flex items-center gap-2 text-xs py-1 px-2 pl-9"
                            data-testid={`settle-history-${s.id}`}
                          >
                            <span className={`font-mono font-bold w-20 ${itemNeg ? "text-cyan-400" : "text-emerald-400"}`}>
                              {fmt(s.amount)}
                            </span>
                            <span className="text-muted-foreground text-[10px] font-mono">
                              {new Date(s.created_at).toLocaleTimeString("es-ES", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false })}
                            </span>
                            {s.created_by_name && (
                              <span
                                className="text-[10px] text-sky-400/90 font-medium truncate max-w-[110px]"
                                title={`Registrado por ${s.created_by_name}`}
                                data-testid={`settle-history-by-${s.id}`}
                              >
                                por {s.created_by_name}
                              </span>
                            )}
                            <span className="flex-1 truncate text-muted-foreground">{s.notes || "—"}</span>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              onClick={() => removeHistory(s.id)}
                              data-testid={`settle-history-delete-${s.id}`}
                              title="Eliminar liquidación"
                            >
                              <Trash2 className="w-3 h-3 text-red-400" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-border/50 flex-shrink-0 bg-card">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="settle-cancel">Cancelar</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className={submitClass}
            data-testid="settle-submit"
          >
            <SubmitIcon className="w-4 h-4 mr-1" /> {busy ? "Guardando…" : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
